import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Search } from 'lucide-react';
import type { JobRole } from '@hcc/types';
import { ApiError } from '../../lib/api';
import { useRequestRole } from '../../hooks/useRoles';

/**
 * Search-as-you-type role picker for person intake. Suggestions come from
 * the SAP-synced role list; a name that matches nothing can be added on the
 * spot, which creates the role as "pending creation in SAP" and notifies
 * the service desk. Free text never silently becomes a person's role: it
 * always goes through a suggestion or the explicit add row.
 */
export function RoleCombobox({
  id,
  roles,
  valueId,
  onChange,
}: {
  id?: string;
  roles: JobRole[];
  valueId: string;
  onChange: (roleId: string) => void;
}) {
  const request = useRequestRole();
  const selected = roles.find((r) => r.id === valueId) ?? null;

  const [query, setQuery] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const blurTimer = useRef<number | null>(null);

  const text = query ?? selected?.name ?? '';
  const trimmed = (query ?? '').trim();

  const matches = useMemo(() => {
    if (!trimmed) return roles;
    const q = trimmed.toLowerCase();
    return roles.filter((r) => r.name.toLowerCase().includes(q));
  }, [roles, trimmed]);

  const exact = matches.some((r) => r.name.toLowerCase() === trimmed.toLowerCase());
  const canAdd = trimmed.length > 0 && !exact;
  // Options: matching roles, then the add row (when eligible).
  const optionCount = matches.length + (canAdd ? 1 : 0);

  function close() {
    setOpen(false);
    setQuery(null);
    setHighlight(0);
  }

  function pick(role: JobRole) {
    onChange(role.id);
    close();
  }

  async function addNew() {
    try {
      const res = await request.mutateAsync(trimmed);
      onChange(res.role.id);
      toast.success(
        res.created
          ? `"${res.role.name}" added. It is pending creation in SAP and IT support has been notified.`
          : `"${res.role.name}" already existed and has been selected.`,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not add the role');
    }
    close();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, optionCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight < matches.length && matches[highlight]) pick(matches[highlight]);
      else if (canAdd) void addNew();
    } else if (e.key === 'Escape') {
      close();
    }
  }

  return (
    <div className="relative">
      <div className="flex items-center">
        <Search className="pointer-events-none absolute left-3 h-4 w-4 text-fg-dim" />
        <input
          id={id}
          value={text}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          placeholder="Search roles, or type a new one"
          className="block h-10 w-full bg-transparent pl-9 pr-3 text-sm outline-none"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Delay so a click on an option lands before the list unmounts.
            blurTimer.current = window.setTimeout(close, 150);
          }}
          onKeyDown={onKeyDown}
        />
      </div>
      {open ? (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-token-sm border border-line-strong/20 bg-surface py-1 shadow-token"
          onMouseDown={() => {
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
          }}
        >
          {valueId ? (
            <li
              role="option"
              aria-selected={false}
              className="cursor-pointer px-3 py-2 text-sm text-fg-muted hover:bg-surface-2"
              onClick={() => {
                onChange('');
                close();
              }}
            >
              No role
            </li>
          ) : null}
          {matches.map((r, i) => (
            <li
              key={r.id}
              role="option"
              aria-selected={r.id === valueId}
              className={
                'cursor-pointer px-3 py-2 text-sm text-fg hover:bg-surface-2 ' +
                (i === highlight ? 'bg-surface-2' : '')
              }
              onClick={() => pick(r)}
            >
              {r.name}
              {r.external_ref && r.sap_synced_at ? (
                <span className="ml-2 font-mono text-xs text-fg-dim">SAP {r.external_ref}</span>
              ) : (
                <span className="ml-2 text-xs text-fg-dim">not in SAP</span>
              )}
            </li>
          ))}
          {canAdd ? (
            <li
              role="option"
              aria-selected={false}
              className={
                'cursor-pointer border-t border-divider/10 px-3 py-2 text-sm text-accent hover:bg-surface-2 ' +
                (highlight === matches.length ? 'bg-surface-2' : '')
              }
              onClick={() => void addNew()}
            >
              <Plus className="mr-1 inline h-3.5 w-3.5" />
              Add "{trimmed}" as a new role (needs creating in SAP)
            </li>
          ) : null}
          {matches.length === 0 && !canAdd ? (
            <li className="px-3 py-2 text-sm text-fg-dim">No roles found</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
